import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { formatEuroTruncated } from "@/lib/formatUtils";
import { SLIDER_CONSTANTS } from "@/utils/paymentModalityCalculations";

type PricingMode = "a_la_carte" | "pack_ailix_1an" | "pack_ailix_2ans";

interface PaymentModalitySliderProps {
  developers: number;
  isCustom: boolean;
  pricingMode: PricingMode;
  sliderStep: number;
  /** Mensualité de comparaison affichée (base 18j/dev/mois + prestations du mode) */
  monthlyCost: number;
  onDevelopersChange: (value: number[]) => void;
}

export function PaymentModalitySlider({
  developers,
  isCustom,
  pricingMode,
  sliderStep,
  monthlyCost,
  onDevelopersChange,
}: PaymentModalitySliderProps) {
  const sliderMarks = useMemo(() => {
    const markValues = [1, 2, 3, 4];
    const { MIN_DEVS, MAX_DEVS } = SLIDER_CONSTANTS;

    return markValues.map((value) => {
      const position = ((value - MIN_DEVS) / (MAX_DEVS - MIN_DEVS)) * 100;
      const isMainMark = value === 1 || value === 2 || value === 4;

      return (
        <div
          key={`slider-mark-${value}`}
          className="absolute flex flex-col items-center"
          style={{ left: `${position}%`, transform: "translateX(-50%)" }}
        >
          <div className={`w-px ${isMainMark ? "h-3 bg-slate-400" : "h-2 bg-slate-300"}`} />
          <span
            className={`text-xs mt-1 whitespace-nowrap ${
              value === 2 ? "text-primary font-medium" : "text-slate-500"
            }`}
          >
            {value} dev{value > 1 ? "s" : ""}
          </span>
        </div>
      );
    });
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Taille de l'équipe</label>
        <div className="flex items-center gap-2">
          {isCustom && <Badge variant="secondary" className="text-xs">Personnalisé</Badge>}
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Mensualité (comparaison)</div>
            <div className="text-2xl font-bold text-primary">{formatEuroTruncated(monthlyCost)}</div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Slider
          value={[developers]}
          onValueChange={onDevelopersChange}
          min={SLIDER_CONSTANTS.MIN_DEVS}
          max={SLIDER_CONSTANTS.MAX_DEVS}
          step={sliderStep}
          className="w-full"
        />
        <div className="relative px-3 h-6">{sliderMarks}</div>
      </div>
    </div>
  );
}

export default PaymentModalitySlider;
